import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, Wallet, LogIn } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import LandingBottomNav from "@/components/LandingBottomNav";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Auth-gated redirect for the bottom-nav "Spending" and "Budget" tabs.
 *
 * - If the user is NOT signed in: show a full-screen prompt card with a
 *   Google Sign-In button explaining what they'll unlock.
 * - If the user IS signed in AND has uploaded at least one report: redirect
 *   to /dashboard/{latestReportId}#{tab}.
 * - If the user IS signed in but has no report yet: show a "Upload first"
 *   card with a CTA to the upload page.
 *
 * Used from two routes (/spending, /budget) via the `tab` prop.
 */
const TAB_CONFIG = {
  spending: {
    icon: BarChart3,
    pageTitle: "Your spending breakdown",
    signInHeading: "Sign in to see your spending breakdown.",
    signInBody: "Upload your first invoice to get started.",
    uploadHeading: "No spending data yet.",
    uploadBody:
      "Upload an invoice or a screenshot and your spending breakdown will appear here.",
  },
  budget: {
    icon: Wallet,
    pageTitle: "Your monthly food budget",
    signInHeading: "Sign in to track your monthly food budget.",
    signInBody: "CartIQ 360 watches every rupee and warns you before you overshoot.",
    uploadHeading: "No budget to track yet.",
    uploadBody:
      "Upload your first invoice and CartIQ 360 will start tracking your budget from there.",
  },
};

export default function IntentGate({ tab }) {
  const navigate = useNavigate();
  const { user, meta, loading } = useAuth();
  const cfg = TAB_CONFIG[tab] || TAB_CONFIG.spending;
  const Icon = cfg.icon;

  // If signed in and we have a latestReportId → redirect straight into the
  // dashboard at the matching hash anchor. Use replace so the Back button
  // takes the user to the previous page, not this gate.
  useEffect(() => {
    if (!loading && user && meta?.latestReportId) {
      navigate(`/dashboard/${meta.latestReportId}#${tab}`, { replace: true });
    }
  }, [loading, user, meta, tab, navigate]);

  return (
    <div
      className="cartiq-wellness min-h-screen bg-[#F8FAFC] text-[#334155] relative pb-20 md:pb-0"
      data-testid={`${tab}-gate-page`}
    >
      <header className="border-b border-[#2A2A35] bg-[#0A0A0F]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <BrandMark />
          <Link
            to="/"
            className="text-[10px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em] font-bold px-2 sm:px-3 py-1.5 sm:py-2 border border-[#2A2A35] text-[#888] hover:text-[#F0EDE8] hover:border-[#F0EDE8] transition-colors whitespace-nowrap"
            data-testid={`${tab}-gate-back`}
          >
            Home
          </Link>
        </div>
      </header>

      <section className="max-w-xl mx-auto px-6 pt-20 md:pt-28 pb-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/40 mb-6">
          <Icon className="w-8 h-8 text-[#FF6B00]" strokeWidth={1.6} />
        </div>

        {loading ? (
          <div className="text-sm text-[#666]">Loading…</div>
        ) : !user ? (
          // ─── Not signed in ────────────────────────────────────────────
          <>
            <h1
              className="font-display uppercase text-[clamp(26px,4vw,38px)] leading-[1.1] tracking-tight"
              data-testid={`${tab}-gate-signin-heading`}
            >
              {cfg.signInHeading}
            </h1>
            <p className="mt-3 text-sm md:text-base text-[#888] max-w-md mx-auto">
              {cfg.signInBody}
            </p>
            <div className="mt-8 flex justify-center" data-testid={`${tab}-gate-signin-btn`}>
              <GoogleAuthButton target={`/${tab}`} />
            </div>
            <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#555]">
              <LogIn className="w-3 h-3" />
              Takes 10 seconds · No signup form
            </div>
          </>
        ) : (
          // ─── Signed in, no report yet ─────────────────────────────────
          <>
            <h1
              className="font-display uppercase text-[clamp(26px,4vw,38px)] leading-[1.1] tracking-tight"
              data-testid={`${tab}-gate-upload-heading`}
            >
              {cfg.uploadHeading}
            </h1>
            <p className="mt-3 text-sm md:text-base text-[#888] max-w-md mx-auto">
              {cfg.uploadBody}
            </p>
            <div className="mt-8 flex justify-center">
              <Link
                to="/upload"
                className="btn-saffron inline-flex items-center justify-center gap-2 px-7 py-3"
                data-testid={`${tab}-gate-upload-cta`}
              >
                Upload my first invoice
              </Link>
            </div>
          </>
        )}
      </section>

      <LandingBottomNav />
    </div>
  );
}
